import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  PropertyUsp1xPhotoInitModel,
  PropertyUsp1xPhotoInitVariables,
  PropertyUsp1xPhotoModel,
  PropertyUsp1xPhotoVariables,
} from '../../../../models/email-template/property-usp-1x-photo.interface';

/**
 * Mockup-styled Property Name + USP + Photo block - a drop-in replacement
 * for app-propertyusp1xphoto in block-renderer-v2 (same [block]/[isPreview]
 * inputs and outputs), so it covers both the center-canvas summary
 * (isPreview=true) and the right-panel edit form (isPreview=false). Reuses
 * the real, unmodified app-block-text (image upload/segment/validation) and
 * app-field-editor-v2/app-pin sub-components and the exact same
 * PropertyUsp1xPhotoVariables field names - only the surrounding layout is
 * new. No CTA Link/CTA Text edit fields, matching v1 exactly (those
 * variables exist on the model and render in the preview if already set,
 * but v1 never exposed an editor for them either).
 */
@Component({
  selector: 'app-property-usp-1x-photo-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './property-usp-1x-photo-v2.component.html',
  styleUrl: './property-usp-1x-photo-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PropertyUsp1xPhotoComponentV2 extends BaseComponent implements OnInit {
  @Input() block: PropertyUsp1xPhotoModel = new PropertyUsp1xPhotoInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;
  /** Center-canvas summary shows the desktop OR mobile image to match the
   *  canvas's own device toggle, not both stacked (see main-banner-v2). */
  @Input() deviceMode: 'desktop' | 'mobile' = 'desktop';

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new PropertyUsp1xPhotoInitVariables();
    }
  }

  imageDesktopPlaceholder = 'Desktop Image';
  imageMobilePlaceholder = 'Mobile Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt Text';
  namePlaceholder = 'Enter Property Name';
  usp1Placeholder = 'Enter USP 1';
  usp2Placeholder = 'Enter USP 2';
  usp3Placeholder = 'Enter USP 3';

  /** Same checkmark asset v1 renders in its own preview (production URL). */
  protected readonly checkmarkIconUrl =
    'https://email.valamar.com/assets/responsysimages/content/valamarri/afasfafsaf244.png';

  protected desktopImageAsUpload = true;
  protected mobileImageAsUpload = true;

  protected toggleDesktopImageMode(): void {
    this.desktopImageAsUpload = !this.desktopImageAsUpload;
  }

  protected toggleMobileImageMode(): void {
    this.mobileImageAsUpload = !this.mobileImageAsUpload;
  }

  get currentDeviceImage(): string | undefined {
    return this.block.variables?.[this.currentDeviceImageKey];
  }

  /** The variable the canvas image slot shows and replaces - an upload in
   *  mobile view replaces the mobile image, not the desktop one. */
  get currentDeviceImageKey(): 'PropertyUsp1xImageMobile' | 'PropertyUsp1xImgDesktop' {
    return this.deviceMode === 'mobile' ? 'PropertyUsp1xImageMobile' : 'PropertyUsp1xImgDesktop';
  }

  get previewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['PropertyUsp1xImageHref']);
    const segment = this.block.variables?.['PropertyUsp1xImageHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof PropertyUsp1xPhotoVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
